import React from 'react';
import { flow } from 'lodash';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Image } from 'react-bootstrap';

import colours from '~/utils/colours';
import { getProfileName } from '~/BonjorosList/util';
import ProfileImage from '~/App/Components/ProfileImage';
import Replaceable from '~/components/higher-order/ReplaceableComponents';
import Styleable, {
  createStyledComponent,
} from '~/App/Components/HOC/Styleable';

const IMAGE_SIZE_PX = 40;

const Container = styled(require('react-bootstrap').ListGroupItem)`
  padding: 0;
  border-color: ${colours.greyMedium};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const styles = {
  container: () => ({}),

  item: ({ props }) => ({
    height: '55px',
    border: 'none',
    borderRadius: 0,
    borderLeft: `2px solid ${props.selected ? colours.blue : 'transparent'}`,
    cursor: props.onClick != null ? 'pointer' : 'inherit',
    width: '100%',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    transitionProperty: 'border-color',
    transitionDuration: '.25s',
  }),

  inner: () => ({
    flexGrow: 1,
    display: 'flex',
    fontWeight: 'bold',
    flexDirection: 'row',
    justifyContent: 'space-between',
  }),

  textContainer: () => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    marginRight: '7px',
    flexGrow: 1,
    marginLeft: '10px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: colours.textDark,
  }),

  title: () => ({
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }),

  imageContainer: () => ({
    display: 'flex',
    marginLeft: '7px',
    flexDirection: 'column',
    justifyContent: 'center',
  }),

  descriptionContainer: () => ({}),

  description: () => ({
    zIndex: 2,
    lineHeight: '25px',
    maxHeight: '25px',
    fontSize: '13px',
    color: colours.greyMediumDark,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginLeft: 0,
    textIndent: 0,
    fontWeight: 'normal',
  }),
};

export const components = {
  Container,

  Item: createStyledComponent('div', 'item'),

  Inner: createStyledComponent('div', 'inner'),

  Title: createStyledComponent('span', 'title'),

  TextContainer: createStyledComponent('div', 'textContainer'),

  Image,
  ImageContainer: createStyledComponent('div', 'imageContainer'),

  Description: createStyledComponent('span', 'description'),
  DescriptionContainer: createStyledComponent('div', 'descriptionContainer'),
};

function ContactImage({ profile, imageUrl, components }) {
  const { Image } = components;

  if (profile) {
    return <ProfileImage profile={profile} size={IMAGE_SIZE_PX} />;
  }

  return <Image src={imageUrl} circle width={IMAGE_SIZE_PX} />;
}

ContactImage.propTypes = {
  profile: PropTypes.object,
  imageUrl: PropTypes.string,
  components: PropTypes.any,
};

function ContactListItem({
  className,
  description,
  imageUrl,
  getStyles,
  selected = false,
  onClick,
  title,
  components,
  profile,
}) {
  const {
    Container,
    Item,
    Description,
    DescriptionContainer,
    Title,
    ImageContainer,
    Inner,
    TextContainer,
  } = components;

  return (
    <Container getStyles={getStyles}>
      <Item
        onClick={onClick}
        className={className}
        getStyles={getStyles}
        selected={selected}
      >
        <Inner getStyles={getStyles}>
          <ImageContainer getStyles={getStyles}>
            <ContactImage
              profile={profile}
              imageUrl={imageUrl}
              components={components}
            />
          </ImageContainer>
          <TextContainer getStyles={getStyles}>
            <Title getStyles={getStyles}>
              {title || getProfileName(profile)}
            </Title>
            <DescriptionContainer getStyles={getStyles}>
              <Description getStyles={getStyles}>
                {description || profile.work.current}
              </Description>
            </DescriptionContainer>
          </TextContainer>
        </Inner>
      </Item>
    </Container>
  );
}

ContactListItem.propTypes = {
  profile: PropTypes.object,
  description: PropTypes.string,
  title: PropTypes.string,
  imageUrl: PropTypes.string,

  onClick: PropTypes.func,
  getStyles: PropTypes.func,
  className: PropTypes.string,
  components: PropTypes.any,
  selected: PropTypes.bool,
};

export default styled(
  flow(Styleable(styles), Replaceable(components))(ContactListItem)
)``;
